import { useMutation, useQueryClient } from '@tanstack/react-query';

import { meQueryKey, usersQueryKey } from '@/api/queryKeys';
import { api } from '@/lib/api-client';
import { toaster } from '@/lib/toaster';

import { membersQueryKey } from './queryKeys';

/** A Grant is one Role on one (game, sandbox) pair, '*' being the wildcard. */
export interface GrantParams {
  user_id: number;
  role_id: string;
  game_code: string;
  sandbox_code: string;
}

// POST /v1/users/{userId}/roles -> 201, empty body. Re-granting an identical live
// Grant is a no-op server-side, so no duplicate can be created.
const grantRole = ({ user_id, ...body }: GrantParams) =>
  api.post(`/v1/users/${user_id}/roles`, body);

// DELETE /v1/users/{userId}/roles/{roleId}?game_code&sandbox_code -> 204, 404 when
// no such live Grant exists. The scope is mandatory: the same Role can be granted
// once per scope, and only the Grant on that exact scope is revoked.
const revokeRole = ({ user_id, role_id, ...scope }: GrantParams) =>
  api.delete(
    `/v1/users/${user_id}/roles/${role_id}?${new URLSearchParams(scope).toString()}`,
  );

// ponytail: plain queryClient, not useInvalidateAppQuery — the latter calls
// useSandboxContext(), which throws on /:gameCode/access/*. Same reason
// useMembers bypasses useAppQuery.
//
// Three keys, because a Grant is visible in three places: the directory list, the
// subject's own detail (the sheet reads it back rather than patching a captured
// copy), and the viewer's own profile — a Permission Manager may grant to
// themselves (#419 AC 8), and every gate in the Access area reads /v1/users/me.
// Left alone the last one stays stale for the 2 min default staleTime, with
// refetchOnWindowFocus off, so your own scope would not follow your own edit
// (#419 AC 13).
export const useInvalidateAccess = (gameCode: string) => {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({
      queryKey: membersQueryKey.all(gameCode),
    });
    void queryClient.invalidateQueries({ queryKey: usersQueryKey.all });
    void queryClient.invalidateQueries({ queryKey: meQueryKey.all });
  };
};

export const useGrantRole = (gameCode: string) => {
  const invalidateAccess = useInvalidateAccess(gameCode);

  return useMutation({
    mutationFn: grantRole,
    onSuccess: () => {
      toaster.success('Role Grant added');
      invalidateAccess();
    },
  });
};

export const useRevokeRole = (gameCode: string) => {
  const invalidateAccess = useInvalidateAccess(gameCode);

  return useMutation({
    mutationFn: revokeRole,
    onSuccess: () => {
      toaster.success('Role Grant removed');
      invalidateAccess();
    },
  });
};
